import { useHelpfulLinks } from "js/placeholderData/helpfulLinks";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Tab } from "@headlessui/react";
import { useRowPagination } from "hooks/useRowPagination";
import { stringFormatingToPascal } from "js/formatting";
import { useSelector } from "react-redux";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import config from "config/config";
import { getTypeOfPolicy } from "js/utilities";
import { useTranslation, Trans } from "react-i18next";
import {
  EverlakeLoadingSpinner,
  EverlakeLoadingOverlayStyle,
} from "components/General/EverlakeLoading";
import moment from "moment";
import Carousel from "components/General/Carousel";
import HelpfulLinks from "components/HelpfulLinks";
import PolicyCard from "components/PolicyCard";
import SimpleDataRow from "components/General/SimpleDataRow";
import AnnuityPolicyCard from "components/AnnuityPolicyCard";
import LoadingOverlay from "react-loading-overlay";
import { useAuthorizationCheck } from "hooks/useAuthorizationCheck";
import UserNotificationDialogOnLogin from "components/Profile/Dialogs/UserNotificationDialogOnLogin";
import { getTasks } from "service/policy/getTasks";
import CustomerRowPagination from "components/General/CustomerRowPagination";

export default function Policies() {
  LoadingOverlay.propTypes = undefined;
  // setup breadcrumb
  useBreadcrumb([]);
  const { t } = useTranslation();
  const [policiesDisplay, setPoliciesDisplay] = useState("card");
  const { policies, claims } = useSelector((state) => state.data);
  const helpfulLinks = useHelpfulLinks(policies);
  const { user } = useSelector((s) => s.user);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [annuityPayoutIndicatorFlag, setAnnuityPayoutIndicatorFlag] =
    useState(false);
  const [openUserNotificationDialog, setOpenUserNotificationDialog] =
    useState(false);
  const [firstName, setFirstName] = useState();
  let viewServiceandClaim = useAuthorizationCheck(4);
  let viewThingsYouCanDo = useAuthorizationCheck(6);
  const getOldPolicy = (contractID) => {
    let policy = policies?.find((p) => p.contractID === contractID);
    if (policy) return policy.oldPolicy;
    return "";
  };

  useEffect(() => {
    if (policies?.length > 0) {
      setIsLoading(true);
      let party = policies[0]?.parties?.find(
        (x) => x.person?.taxId === user.identityNumber
      );
      setFirstName(stringFormatingToPascal(party?.person?.firstName));
      if (
        config.E_DELIVERY_ENABLE === "true" &&
        user.notificationPreference === 0
      ) {
        setOpenUserNotificationDialog(true);
      }
      let policyIds = policies.map((p) => p.contractID);
      let annuityPayoutIndicatorFlag = policies.map(
        (p) => p.annuityPayoutIndicatorFlag
      );
      setAnnuityPayoutIndicatorFlag(
        annuityPayoutIndicatorFlag.every((ele) => ele === "Y")
      );
      getTasks({
        contractId_array: policyIds,
        agentId: "",
      })
        .then((response) => {
          const restrictedProcessServices =
            config.RESTRICTED_PROCESS_SERVICE_REQUEST.split(",");
          let serviceList = [];
          //sort newest to oldest
          response?.sort(function (a, b) {
            if (new Date(a.pendingDate) > new Date(b.pendingDate)) {
              return -1;
            }
            if (new Date(a.pendingDate) < new Date(b.pendingDate)) {
              return 1;
            }
            return 0;
          });
          response?.forEach((task) => {
            if (
              !restrictedProcessServices.includes(task.process.toLowerCase()) &&
              task?.status?.toLowerCase() !== "e"
            )
              serviceList.push({
                desc: task.desc,
                oldPolicy: getOldPolicy(task.policyID.substring(2)),
                status: task.status,
                contractID: task.policyID.substring(2),
                taskID: task.taskID,
                pendingDate: task.pendingDate,
                openedDate: task.openedDate,
                closedDate: task.closedDate,
              });
          });

          let pendingStatus = serviceList
            .filter((s) => s.status.toLowerCase() !== "c")
            .slice(0, 4);
          let complatedStatus = serviceList.filter(
            (s) => s.status.toLowerCase() === "c"
          );

          if (serviceList?.length > 0)
            setServices(pendingStatus.concat(complatedStatus).slice(0, 4));
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(
            "Server was unable to connect, please try again later.",
            error
          );
          setIsLoading(false);
        });
    }
  }, [policies, user]);

  const { rowsForDisplay: current, rowPaginationProps: paginationProps } =
    useRowPagination(policies, [3, 10, 25, "all"]);
  return (
    <>
      {user?.userId && (
        <>
          <LoadingOverlay
            active={isLoading}
            spinner={
              <EverlakeLoadingSpinner
                loadingText={t("CustomerDashboard-loadingMessage")}
              />
            }
            styles={EverlakeLoadingOverlayStyle}
          >
            <UserNotificationDialogOnLogin
              showDialog={openUserNotificationDialog}
              setShowDialog={setOpenUserNotificationDialog}
            />
            <div className="container mb-8">
              <h1>
                {t("CustomerDashboard-welcomeMessage", {
                  name: firstName
                    ? stringFormatingToPascal(firstName)
                    : t("CustomerPolicies-Greating"),
                })}
              </h1>
              <hr />
            </div>

            <Carousel policies={policies} />

            <div className="container ui-component-spacing">
              <div className="ui-policy-grid">
                <div className="flex flex-row items-center justify-between p-4 md:p-0 lg:block">
                  <h2 className="text-xl md:text-3xl">
                    {t("CustomerDashboard-policyTitle")}
                  </h2>
                  <div className="hidden md:flex md:justify-between md:items-start">
                    <p className="hidden text-sm mr-6 lg:block">
                      <Trans
                        i18nKey="CustomerDashboard-policySubTitle"
                        values={{ date: moment().format("MM/DD/YYYY") }}
                      />
                    </p>
                  </div>
                </div>

                <hr className="hidden md:block" />
                {current?.length > 0 ? (
                  <>
                    <div
                      className={`${
                        policiesDisplay === "card"
                          ? "ui-grid-cards"
                          : "ui-grid-table"
                      }`}
                    >
                      {current.map((policy, index) => {
                        const policyType = getTypeOfPolicy(
                          policy?.product?.type
                        );
                        if (
                          config.ENABLE_ANNUITIES === "true" &&
                          policyType === "annuity"
                        ) {
                          return (
                            <AnnuityPolicyCard
                              data={policy}
                              key={`Annuity policy - ${index}`}
                            />
                          );
                        } else
                          return (
                            <PolicyCard
                              data={policy}
                              key={`policy - ${index}`}
                            />
                          );
                      })}
                    </div>
                    {/* <!-- paging component --> */}
                    <div className="flex justify-between items-start mt-6">
                      <div>
                        <p className="hidden text-sm md:block lg:hidden mr-6">
                          <Trans
                            i18nKey="CustomerDashboard-policySubTitle"
                            values={{ date: moment().format("MM/DD/YYYY") }}
                          />
                        </p>
                      </div>

                      <CustomerRowPagination {...paginationProps} />
                    </div>
                    {/* <!-- paging component --> */}
                  </>
                ) : (
                  <>
                    <p className="text-xl font-semibold pb-2">
                      {t("CustomerPolicies-no-policy-title")}
                    </p>
                    <p className="text-sm">
                      {t("CustomerPolicies-no-policy-subtitle")}
                    </p>
                  </>
                )}
              </div>
              <p className="block mt-6 text-sm md:hidden">
                <Trans
                  i18nKey="CustomerDashboard-policySubTitle"
                  values={{ date: moment().format("MM/DD/YYYY") }}
                />
              </p>
            </div>
            <div className="container">
              <div className={`lg:grid lg:gap-x-4 lg:grid-cols-3 -mb-8`}>
                {/* <!-- Quick Links --> */}
                {viewThingsYouCanDo && (
                  <div>
                    <h2>{t("CustomerDashboard-sectionTwoTitle")}</h2>
                    <p className="text-sm">
                      {t("CustomerDashboard-sectionTwoSubTitle")}
                    </p>
                    <hr />

                    <HelpfulLinks links={helpfulLinks} />
                  </div>
                )}
                {/* <!-- Quick Links --> */}
                {/* <!-- My  Claims --> */}
                {!annuityPayoutIndicatorFlag && viewServiceandClaim && (
                  <div>
                    <div role="tablist" aria-label="My Requests &amp; Claims">
                      <h2>{t("CustomerDashboard-sectionThreeTitle")}</h2>
                      <p className="text-sm">
                        {t("CustomerDashboard-sectionThreeSubTitle")}
                      </p>
                      <hr />
                    </div>
                    <div className="ui-card ui-component-spacing md:flex md:flex-col md:justify-between">
                      <div className="font-semibold p-4 pb-2 hidden md:block">
                        {t("CustomerDashboard-sectionThreeTitle2")}
                      </div>
                      {claims.length > 0 &&
                        claims
                          .slice(0, 2)
                          .map((claim, idx) => (
                            <SimpleDataRow
                              type="customerServicesAndClaims"
                              key={`Customer claim - ${idx}`}
                              data={claim}
                            />
                          ))}
                      <Link
                        to="/services-claims/claims"
                        className="text-center ui-card-btn-more"
                        aria-label="View more service requests"
                      >
                        {t("CustomerDashboard-sectionTwoView")}
                      </Link>
                    </div>
                  </div>
                )}

                {/* <!-- My Service --> */}
                {viewServiceandClaim && (
                  <div
                    className={`${
                      annuityPayoutIndicatorFlag ? "col-span-2" : "col-span-1"
                    }`}
                  >
                    <div aria-label="My Requests &amp; Claims">
                      <h2>
                        {t("CustomerDashboard-sectionThreeServiceRequestTitle")}
                      </h2>
                      <p className="text-sm">
                        {t(
                          "CustomerDashboard-sectionThreeServiceRequestSubTitle"
                        )}
                      </p>
                      <hr />
                    </div>
                    <div className="ui-card ui-component-spacing md:flex md:flex-col md:justify-between">
                      {services.length > 0 &&
                        services.map((claim, idx) => (
                          <>
                            <SimpleDataRow
                              type="services"
                              key={`Customer services - ${idx}`}
                              data={claim}
                            />
                          </>
                        ))}
                      <Link
                        to="/services-claims/services"
                        className="text-center ui-card-btn-more"
                        aria-label="View more service requests"
                      >
                        {t("CustomerDashboard-sectionTwoView")}
                      </Link>
                    </div>
                  </div>
                )}
                {/* <!-- My Requests & Claims --> */}
              </div>
              <hr></hr>
              <div className="text-center text-xs text-theme-text-secondary px-4">
                {t("CustomerDashboard-infoDescription")}
              </div>
            </div>
          </LoadingOverlay>
        </>
      )}
    </>
  );
}
